import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  linkedSignal,
  viewChild,
  computed,
} from '@angular/core';
import { CoinflipCoinComponent } from '@features/games/coinflip/coinflip-coin/coinflip-coin.component';

import { CoinflipCoin } from '@features/games/coinflip/types/coins.type';
import { ModalService } from '@core/modals/services/modal.service';
import { CoinflipDeprecatedService } from '@features/games/coinflip/coinflip.service';
import 'number-flow';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { CostComponent } from '@core/components/cost/cost.component';
import {
  CoinflipSkinItem,
  CoinflipWon,
} from '@features/games/coinflip/interfaces/coinflip.interface';
import { normalizeSkinImage } from '@core/utils/skins.util';
import { SvgIconComponent } from 'angular-svg-icon';

interface NumberFlowElement extends HTMLElement {
  value?: number;
  update: (value: number) => void;
  format?: any;
  trend?: number;
}

@Component({
  selector: 'app-coinflip-won-modal',
  templateUrl: './coinflip-won-modal.component.html',
  styleUrls: ['./coinflip-won-modal.component.scss'],
  standalone: true,
  imports: [
    CoinflipCoinComponent,
    CostComponent,
    NgxParticlesModule,
    SvgIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'modal-container flex flex-col',
    '[class.blue]': 'this.coinflipWonCoin() === 1',
    '[class.red]': 'this.coinflipWonCoin() === 2',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoinflipWonModalComponent {
  public modalService = inject(ModalService);
  private coinflipService = inject(CoinflipDeprecatedService);
  private ngParticlesService = inject(NgParticlesService);
  private destroyRef = inject(DestroyRef);

  readonly coinflipGame = input.required<CoinflipWon>();
  readonly skins = input.required<CoinflipSkinItem[]>();
  readonly coinflipWonCoin = input.required<CoinflipCoin>();

  skinsSum = computed(() => {
    return this.skins().reduce((acc, skin) => acc + skin.price, 0);
  });

  skinsWithImages = computed(() => {
    return this.skins().map((s) => ({
      ...s,
      image: normalizeSkinImage(s.image),
    }));
  });

  countdownDisplayElementRef = viewChild.required<
    ElementRef<NumberFlowElement>
  >('countdownDisplayElement');

  selectedCoin = linkedSignal<CoinflipCoin>(() => this.coinflipWonCoin());
  initialCountdownValue = 30;
  countdownDuration = signal(this.initialCountdownValue);

  private timerInterval: any;

  particlesOptions: ISourceOptions = {
    fullScreen: { enable: false },
    particles: {
      number: {
        value: 20,
      },
      color: {
        value: ['#ffffff'],
      },
      move: {
        direction: 'none',
        enable: true,
        outModes: {
          default: 'out',
        },
        random: true,
        speed: 1.5,
        straight: false,
      },
      opacity: {
        value: { min: 0.17, max: 0.5 },
      },
      size: {
        value: { min: 1, max: 1.2 },
      },
      wobble: {
        enable: false,
      },
    },
  };

  ngOnInit() {
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });
  }

  ngAfterViewInit() {
    const element = this.countdownDisplayElementRef().nativeElement;
    element.format = { minimumIntegerDigits: 2 };
    this.updateNumberFlowDisplay(element, this.initialCountdownValue);

    this.timerInterval = setInterval(() => {
      const newDuration = this.countdownDuration() - 1;
      this.countdownDuration.set(newDuration);
      this.updateNumberFlowDisplay(element, newDuration >= 0 ? newDuration : 0);

      if (newDuration < 0) {
        this.modalService.close();
      }
    }, 1000);

    this.destroyRef.onDestroy(() => clearInterval(this.timerInterval));
  }

  updateNumberFlowDisplay(element: NumberFlowElement, currentValue: number) {
    element.update(currentValue);
  }

  async doubleDownButton() {
    await this.coinflipService.doubleDown({
      id: this.coinflipGame().gameid,
      side: this.selectedCoin(),
    });
    this.modalService.close();
  }

  public async claimWinningsButton() {
    await this.coinflipService.claimWinnings(this.coinflipGame().gameid);
    this.modalService.close();
  }

  public handleOpenSkinsPopup() {
    this.modalService.openModal('CoinflipSkinsModal', {
      coinflipGame: this.coinflipGame(),
      skins: this.skins(),
      coinflipSide:
        this.coinflipGame().winner.steamid === this.coinflipGame().host.steamid
          ? this.coinflipGame().host
          : this.coinflipGame().part,
    });
  }
}
